import React from "react";
import { useFieldArray, useFormContext, Controller } from "react-hook-form";
import { TextField } from "@mui/material";
import { ManuallyLeadFormValues } from "./ImportLeadCreateManually";

type DictionaryEditorProps = {
    name: "customPropertiesList";
};

/**
 * DictionaryEditor gives the opportunity to create dynamic field.
 */
export function DictionaryEditor({ name }: DictionaryEditorProps) {
    const { control, watch, getValues, reset, formState } =
        useFormContext<ManuallyLeadFormValues>();

    const { fields, append, remove } = useFieldArray({ control, name });
    // Provide a fallback if watch(name) is undefined
    const allCustomProps = watch(name) || [];

    function handleAddField() {
        // Capture the current dirty status
        const wasDirty = formState.isDirty;

        // 1. Append the new field
        append({ key: "", value: "" });

        // 2. If the form was NOT dirty before, redefine the defaults
        if (!wasDirty) {
            const updatedValues = getValues();
            reset(updatedValues);
        }
    }

    function handleRemove(index: number) {
        const { dirtyFields } = formState;

        const dirtyFieldKeys = Object.keys(dirtyFields);

        const filteredKeys = dirtyFieldKeys.filter(
            (key) => key !== "customPropertiesList"
        );

        remove(index);
        const updatedValues = getValues();
        // Check if the remaining custom properties are empty
        const customProps = updatedValues.customPropertiesList || [];
        const allEmpty = customProps.every((item) => {
            const keyEmpty = !item.key || item.key.trim() === "";
            const valueEmpty = !item.value || item.value.trim() === "";
            return keyEmpty && valueEmpty;
        });

        if (filteredKeys.length <= 0 && allEmpty) {
            reset(updatedValues);
        }
    }

    return (
        <div className="dictionary-editor">
            <div className="flex">
                <label className="bolder">Weitere Felder:</label>
                <div className="pull-right">
                    <i
                        className="icons icons-icon-plus-circle cursor-pointer button-success icon-large"
                        onClick={handleAddField}
                    ></i>
                </div>
            </div>
            <div className="dynamic-field-container">
                {fields.map((field, index) => (
                    <div key={field.id}>
                        <div className="flex">
                            <div className="input col-6 mr-15-mobile">
                                <Controller
                                    name={`${name}.${index}.key`}
                                    control={control}
                                    rules={{
                                        validate: {
                                            requiredIfValue: (
                                                keyValue: string
                                            ) => {
                                                if (
                                                    !keyValue?.trim() &&
                                                    allCustomProps[
                                                        index
                                                    ].value?.trim()
                                                ) {
                                                    return "Der Feldname darf nicht leer sein.";
                                                }
                                                return true;
                                            },
                                            unique: (value) => {
                                                // If empty, 'notEmpty' catches it
                                                if (!value?.trim()) return true;
                                                const duplicates =
                                                    allCustomProps.filter(
                                                        (item, i) =>
                                                            i !== index &&
                                                            item.key === value
                                                    );
                                                return (
                                                    duplicates.length === 0 ||
                                                    "Dieses Feld ist bereits vorhanden. Ändern Sie den Feld-Schlüssel."
                                                );
                                            }
                                        }
                                    }}
                                    render={({
                                        field: controllerField,
                                        fieldState: { error }
                                    }) => (
                                        <TextField
                                            {...controllerField}
                                            className="empty-bg-change"
                                            variant="outlined"
                                            fullWidth
                                            margin="normal"
                                            placeholder="Feldname (z.B. Wohnsituation)"
                                            error={!!error}
                                            helperText={
                                                error ? error.message : ""
                                            }
                                            inputProps={{ maxLength: 50 }}
                                        />
                                    )}
                                />
                            </div>
                            <div className="input col-5">
                                <Controller
                                    name={`${name}.${index}.value`}
                                    control={control}
                                    render={({ field: controllerField }) => (
                                        <TextField
                                            {...controllerField}
                                            className="empty-bg-change"
                                            variant="outlined"
                                            fullWidth
                                            margin="normal"
                                            placeholder="Wert (z.B. lebt alleine)"
                                            inputProps={{ maxLength: 50 }}
                                        />
                                    )}
                                />
                            </div>
                            <div className="pull-right cursor-pointer trash-desktop">
                                <i
                                    className="icons icons-icon-trash button-danger icon-large "
                                    onClick={() => handleRemove(index)}
                                ></i>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
